import { isNil } from 'lodash';

import {
  CustomerDraftResponse,
  CustomerEditResponse,
  DurationTypeEnum,
  ExtendedNamedAttachmentModel,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import { CustomerAdditionalInfoResponseModel } from 'api/adminApiClient';
import { ClientInitialValues } from 'components/modals/ClientModal';
import { EditClientAdditionalInfoInitialValues } from 'components/modals/EditClientAdditionalInfoModal';
import { ClientCreationEnum } from 'components/modals/ClientModal/enum';
import { CustomerBulkImportDetailsModel } from '../../ClientBulkImportPage/ClientBulkImportTableSection/interface';
import { AgreementFileValue } from 'components/modals/ClientModal/ServiceAgreement/AgreementUploader/AgreementUploader';

type CustomerBulkImportWithoutIdDetailsModel = Omit<
  CustomerBulkImportDetailsModel,
  'id'
>;

const mapCustomerGeneralInfoToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };

  const { generalInfo } = getCorrectData();

  const customerGeneralInfoInitialValues = {
    companyName: generalInfo?.companyInfo?.companyName || '',
    state: !isNil(generalInfo?.companyInfo?.location?.stateId)
      ? generalInfo?.companyInfo?.location?.stateId
      : undefined,
    city: generalInfo?.companyInfo?.location?.city || '',
    country: !isNil(generalInfo?.companyInfo?.location?.countryId)
      ? generalInfo?.companyInfo?.location?.countryId
      : undefined,
    internationalStateName: !isNil(
      generalInfo?.companyInfo?.location?.internationalStateName,
    )
      ? generalInfo?.companyInfo?.location?.internationalStateName
      : undefined,
    internationalCityName: !isNil(
      generalInfo?.companyInfo?.location?.internationalCityName,
    )
      ? generalInfo?.companyInfo?.location?.internationalCityName
      : undefined,
    mainAddress: generalInfo?.companyInfo?.location?.address || '',
    zip: generalInfo?.companyInfo?.location?.zipCode || '',
    companyWebsite: generalInfo?.companyInfo?.companyWebSite || '',
    additionalInvoiceDBA: generalInfo?.companyInfo?.additionalInvoiceName || '',
    companyEmail: generalInfo?.companyInfo?.companyEmail || '',
    companyPhone: generalInfo?.companyInfo?.companyPhoneNumber || '',
    primaryContacts: !!generalInfo?.primaryContacts?.length
      ? generalInfo?.primaryContacts?.map((contact) => ({
          id: contact.contactId,
          firstName: contact?.firstName || '',
          lastName: contact?.lastName || '',
          title: contact?.title || '',
          email: contact?.email || '',
          phone: contact?.phoneNumber || '',
        }))
      : [
          {
            firstName: '',
            lastName: '',
            title: '',
            email: '',
            phone: '',
          },
        ],
  };

  if (
    creationType === ClientCreationEnum.Edit ||
    creationType === ClientCreationEnum.EditDraft
  ) {
    const { customerGroupId } = customerData;

    return {
      ...customerGeneralInfoInitialValues,
      customerGroupId: !isNil(customerGroupId) ? customerGroupId : undefined,
    };
  }

  if (creationType === ClientCreationEnum.EditImport) {
    const { customerGroupName } = customerData;

    return {
      ...customerGeneralInfoInitialValues,
      customerGroupName: !isNil(customerGroupName)
        ? customerGroupName
        : undefined,
    };
  }

  return customerGeneralInfoInitialValues;
};

const mapCustomerBillingInfoToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };

  const { billingInfo, generalInfo } = getCorrectData();

  return {
    billingContacts: !!billingInfo.billingContacts?.length
      ? billingInfo.billingContacts
      : [
          {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
          },
        ],
    billingLocation: {
      locationSameAsMain: billingInfo?.isBillingSameAsMainAddress || false,
      country: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.countryId
        : billingInfo?.location?.countryId || undefined,
      internationalStateName: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.internationalStateName
        : billingInfo?.location?.internationalStateName || undefined,
      internationalCityName: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.internationalCityName
        : billingInfo?.location?.internationalCityName || undefined,
      state: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.stateId || undefined
        : billingInfo?.location?.stateId || undefined,
      city: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.city || ''
        : billingInfo?.location?.city || '',
      address: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo.companyInfo.location.address || ''
        : billingInfo?.location?.address || '',
      zip: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo.companyInfo.location.zipCode || ''
        : billingInfo?.location?.zipCode || '',
    },
  };
};

const mapCustomerAdditionalInfoToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };

  const { additionalInfo } = getCorrectData();

  return {
    clientSize: !isNil(additionalInfo?.potentialHeadcount)
      ? additionalInfo?.potentialHeadcount
      : undefined,
    industry: additionalInfo?.industryName || '',
    haveBusinessBefore: additionalInfo?.haveBusinessBefore || false,
    yearsInBusiness: !isNil(additionalInfo?.yearsInBusiness)
      ? additionalInfo?.yearsInBusiness
      : undefined,
    abilityToPay: !isNil(additionalInfo?.abilityToPay)
      ? additionalInfo?.abilityToPay
      : undefined,
    billRate: !isNil(additionalInfo?.averageBillRate)
      ? additionalInfo?.averageBillRate
      : null,
    duration: !isNil(additionalInfo?.estimatedValue)
      ? additionalInfo?.estimatedValue
      : null,
    durationUnit: additionalInfo?.estimatedType || DurationTypeEnum.Week,
    clientSuccessPartnerUserId:
      additionalInfo?.mbpContactsInfo?.clientSuccessPartnerUserId || undefined,
    bdSalesContactUserId:
      additionalInfo?.mbpContactsInfo?.bdSalesContactUserId || undefined,
    billingSpecialistUserId:
      additionalInfo?.mbpContactsInfo?.billingSpecialistUserId || undefined,
  };
};

const mapCustomerServiceAgreementToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const isEditImport = creationType === ClientCreationEnum.EditImport;
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };
  const { agreementDetailsInfo } = getCorrectData();

  return {
    serviceAgreement:
      agreementDetailsInfo?.agreementDetailsType ||
      ServiceAgreementTypeEnum.MyBasePayAgreement,
    clientAgreement:
      agreementDetailsInfo?.agreementDetailsType ===
        ServiceAgreementTypeEnum.ClientAgreement &&
      agreementDetailsInfo?.agreementAttachments
        ? agreementDetailsInfo?.agreementAttachments.map(
            (file: AgreementFileValue | ExtendedNamedAttachmentModel) => ({
              fileName: file.fileName || '',
              s3Key: isEditImport
                ? (file as AgreementFileValue).s3Key
                : (file as ExtendedNamedAttachmentModel).fileKey || '',
              createdDate: file?.createdDate || undefined,
            }),
          )
        : [],
    memberAgreement:
      agreementDetailsInfo?.agreementDetailsType ===
        ServiceAgreementTypeEnum.MemberAgreement &&
      agreementDetailsInfo?.agreementAttachments
        ? agreementDetailsInfo?.agreementAttachments.map(
            (file: AgreementFileValue | ExtendedNamedAttachmentModel) => ({
              fileName: file.fileName || '',
              s3Key: isEditImport
                ? (file as AgreementFileValue).s3Key
                : (file as ExtendedNamedAttachmentModel).fileKey || '',
              createdDate: file?.createdDate || undefined,
            }),
          )
        : [],
    myBasePayAgreement:
      agreementDetailsInfo?.agreementDetailsType ===
        ServiceAgreementTypeEnum.MyBasePayAgreement &&
      agreementDetailsInfo?.agreementAttachments
        ? agreementDetailsInfo?.agreementAttachments.map(
            (file: AgreementFileValue | ExtendedNamedAttachmentModel) => ({
              fileName: file.fileName || '',
              s3Key: isEditImport
                ? (file as AgreementFileValue).s3Key
                : (file as ExtendedNamedAttachmentModel).fileKey || '',
              createdDate: file?.createdDate || undefined,
            }),
          )
        : [],
    isAgreementNotAvailableConfirmed:
      agreementDetailsInfo.agreementDetailsType ===
      ServiceAgreementTypeEnum.NotAvailable
        ? agreementDetailsInfo.isAgreementNotAvailableConfirmed
        : undefined,
  };
};

type CustomerResponse = CustomerDraftResponse | CustomerEditResponse;
export const mapCustomerDetailsToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
): ClientInitialValues => {
  return {
    generalInfo: mapCustomerGeneralInfoToInitialValues(
      customerData,
      creationType,
    ),
    billingInfo: mapCustomerBillingInfoToInitialValues(
      customerData,
      creationType,
    ),
    serviceAgreement: mapCustomerServiceAgreementToInitialValues(
      customerData,
      creationType,
    ),
    additionalInfo: mapCustomerAdditionalInfoToInitialValues(
      customerData,
      creationType,
    ),
  };
};

export const mapCustomerAdditionalDetailsToInitialValues = (
  customerAdditionalData: CustomerAdditionalInfoResponseModel,
): EditClientAdditionalInfoInitialValues => {
  const { generalContractInformation, financialInformation, additionalInfo3 } =
    customerAdditionalData;

  return {
    generalContractInfo: {
      specifications:
        generalContractInformation?.insuranceRequirements
          ?.certificateOfInsuranceSpecifications || '',
      certificate: generalContractInformation?.insuranceRequirements
        ?.certificateOfInsuranceCreatedFileKey
        ? [
            generalContractInformation?.insuranceRequirements
              ?.certificateOfInsuranceCreatedFileKey,
          ]
        : [],
      submittedTo:
        generalContractInformation?.insuranceRequirements?.submittedTo || '',

      sowRequired: !isNil(
        generalContractInformation?.insuranceRequirements?.sowRequired,
      )
        ? generalContractInformation?.insuranceRequirements?.sowRequired
        : undefined,
      sowTemplate: generalContractInformation?.insuranceRequirements
        ?.sowTemplateFileKey
        ? [
            generalContractInformation?.insuranceRequirements
              ?.sowTemplateFileKey,
          ]
        : [],
      sowSpecialInstructions:
        generalContractInformation?.insuranceRequirements
          ?.sowSpecialInstructions || '',
      isCreditCheckVerified: !isNil(
        generalContractInformation?.insuranceRequirements
          ?.isCreditCheckVerified,
      )
        ? generalContractInformation?.insuranceRequirements
            ?.isCreditCheckVerified
        : undefined,
    },
    financialInfo: {
      markupRate: financialInformation?.rateOverview?.fixedMarkupRate || '',
      dtBillRateMultiplier: !isNil(
        financialInformation?.rateOverview?.dtBillRateMultiplier,
      )
        ? financialInformation?.rateOverview?.dtBillRateMultiplier
        : null,
      otRateMultiplier:
        financialInformation?.rateOverview?.otRateMultiplier || '',

      creditCheck:
        financialInformation?.financialOverview?.creditCheckScore || '',
      commissionType:
        financialInformation?.financialOverview?.commissionTypeApproval || null,
      notes: financialInformation?.financialOverview?.addedNotes || '',
      paymentTerms:
        financialInformation?.financialOverview?.paymentTerm || null,

      conversion: financialInformation?.rateOverview
        ?.tempToPermConversionFileKey
        ? [financialInformation?.rateOverview?.tempToPermConversionFileKey]
        : [],
      MSPFee:
        typeof financialInformation?.mspOrVmsOverview?.mspFee === 'number'
          ? financialInformation.mspOrVmsOverview.mspFee
          : undefined,
      VMSFee:
        typeof financialInformation?.mspOrVmsOverview?.vmsFee === 'number'
          ? financialInformation.mspOrVmsOverview.vmsFee
          : undefined,
      invoicingInfo:
        financialInformation?.mspOrVmsOverview?.invoicingInformation || '',
      invoicingInstructions:
        financialInformation?.mspOrVmsOverview?.specialInvoicingInstructions ||
        '',
      invoicingCycle:
        financialInformation?.mspOrVmsOverview?.invoicingCycle || null,
      payrollCycle:
        financialInformation?.mspOrVmsOverview?.payrollCycle || null,
      url: financialInformation?.mspOrVmsOverview?.url || '',
      credentials: financialInformation?.mspOrVmsOverview?.credentials || '',
      providerName:
        financialInformation?.mspOrVmsOverview?.mspOrVmsProviderName || '',
      isThirdWorkforce: !isNil(
        financialInformation?.mspOrVmsOverview?.isThereThirdPartyWorkforce,
      )
        ? financialInformation?.mspOrVmsOverview?.isThereThirdPartyWorkforce
        : undefined,
      purchaseOrder: !isNil(
        financialInformation?.mspOrVmsOverview?.purchaseOrder,
      )
        ? financialInformation?.mspOrVmsOverview?.purchaseOrder
        : undefined,
      purchaseOrderFileKey: financialInformation?.mspOrVmsOverview
        ?.purchaseOrderFileKey
        ? [financialInformation?.mspOrVmsOverview?.purchaseOrderFileKey]
        : [],
      purchaseOrderInstructions:
        financialInformation?.mspOrVmsOverview?.purchaseOrderInstructions || '',
      customerCostingMethodType:
        financialInformation?.financialOverview?.customerCostingMethodType ??
        null,
    },
    assignmentRequirements: {
      contractorsPermitted: !isNil(
        additionalInfo3?.assignmentRequirements?.isIndependentContractors,
      )
        ? additionalInfo3?.assignmentRequirements?.isIndependentContractors
        : undefined,
      preScreeningRequired: !isNil(
        additionalInfo3?.assignmentRequirements?.isPreScreeningRequired,
      )
        ? additionalInfo3?.assignmentRequirements?.isPreScreeningRequired
        : undefined,
      preScreeningBilledTo: !isNil(
        additionalInfo3?.assignmentRequirements?.preScreeningBilledTo,
      )
        ? additionalInfo3?.assignmentRequirements?.preScreeningBilledTo
        : undefined,
      preScreening: additionalInfo3?.assignmentRequirements?.preScreening || '',
      designatedPreScreening: !isNil(
        additionalInfo3?.assignmentRequirements?.designatedPreScreeningProvider,
      )
        ? additionalInfo3?.assignmentRequirements
            ?.designatedPreScreeningProvider
        : undefined,
      isOnboardingDocumentationRequired: !isNil(
        additionalInfo3?.assignmentRequirements
          ?.isOnboardingDocumentationRequired,
      )
        ? additionalInfo3?.assignmentRequirements
            ?.isOnboardingDocumentationRequired
        : undefined,
      specialInstructions:
        additionalInfo3?.assignmentRequirements?.specialInstructions || '',
      onboardingDocumentation:
        additionalInfo3?.assignmentRequirements
          ?.onboardingDocumentationFileKeys || [],
      terminationInstructions:
        additionalInfo3?.assignmentRequirements?.terminationInstructions || '',
      workPerformance:
        additionalInfo3?.specialAssignmentRequirements
          ?.guaranteedWorkPerformance || '',
      tempToPermConversion:
        additionalInfo3?.specialAssignmentRequirements?.tempToPermConversion ||
        '',
      timeEntry:
        additionalInfo3?.specialAssignmentRequirements?.timeEntry || '',
      assignmentNotes:
        additionalInfo3?.specialAssignmentRequirements?.addedNotes || '',
      breakInService: !isNil(
        additionalInfo3?.specialAssignmentRequirements?.breakInService,
      )
        ? additionalInfo3?.specialAssignmentRequirements?.breakInService
        : undefined,
    },
  };
};
